import { InvestmentInstructionSubmissionInstructionData } from '../models/submissions/investment-instruction-submission-instruction-data';
import { InvestmentInstructionSubmissionAffectedAccount } from '../models/submissions/investment-instruction-submission-affected-account';
import { AccountType } from '../models/transactions/account-type';
import { AccountTypeIdEnum } from '../models/enum/shared/account-type-id.enum';

export type GroupedInstructionData = {
  accountName?: string;
  accountId?: number;
  instructions: InvestmentInstructionSubmissionInstructionData[];
};

export class InstructionSubmissionUtils {
  static calculateLongestInstructionLength(instructionData: InvestmentInstructionSubmissionInstructionData[]): number {
    const countByAccountId = new Map<number | null, number>();

    for (const item of instructionData) {
      const key = item.accountId;
      countByAccountId.set(key, (countByAccountId.get(key) ?? 0) + 1);
    }
    return Array.from(countByAccountId.values()).reduce((max, count) => {
      return Math.max(max, count);
    }, 0);
  }

  static distributionsAreEqual(instructionData: InvestmentInstructionSubmissionInstructionData[] = []): boolean {
    if (instructionData.length === 0) return true;
    // API sends back instructions with null accountId when the distribution is equal
    return instructionData[0].accountId === null;
  }

  static groupInvestmentInstructionsByAccount(
    instructionData: InvestmentInstructionSubmissionInstructionData[],
    affectedAccounts: InvestmentInstructionSubmissionAffectedAccount[],
    distributionsAreEqual: boolean,
    accountType?: AccountType | null
  ): GroupedInstructionData[] {
    const sortedInstructionData = this.sortInstructionsByFundId(instructionData);
    return distributionsAreEqual
      ? this.groupAsEqualDistribution(sortedInstructionData)
      : this.groupAsUnequalDistributions(sortedInstructionData, affectedAccounts, accountType);
  }

  public static sortInstructionsByFundId(
    instructions: InvestmentInstructionSubmissionInstructionData[]
  ): InvestmentInstructionSubmissionInstructionData[] {
    return instructions.slice().sort((a, b) => {
      return a.fundId - b.fundId;
    });
  }

  private static groupAsEqualDistribution(
    instructionData: InvestmentInstructionSubmissionInstructionData[]
  ): GroupedInstructionData[] {
    return [
      {
        instructions: instructionData
      }
    ];
  }

  public static resolveAccountName(
    account: InvestmentInstructionSubmissionAffectedAccount,
    accountType?: AccountType | null
  ): string {
    const accountTypeName = accountType?.name?.trim() ?? '';
    const employerName = account?.employerName?.trim() ?? '';
    const jurisdiction = account?.governingJurisdiction?.trim() ?? '';

    const isVariableBenefit = accountType ? accountType.id === AccountTypeIdEnum.VariableBenefit : !!jurisdiction;
    if (isVariableBenefit) {
      return `${jurisdiction || $localize`Non-Locked-In`} ${accountTypeName}`.trim();
    }
    return employerName || accountTypeName;
  }

  private static groupAsUnequalDistributions(
    instructionData: InvestmentInstructionSubmissionInstructionData[],
    affectedAccounts: InvestmentInstructionSubmissionAffectedAccount[],
    accountType?: AccountType | null
  ): GroupedInstructionData[] {
    const grouped: Record<number, GroupedInstructionData> = {};

    for (const item of instructionData) {
      if (item.accountId === null) continue;

      const accountId = item.accountId;
      if (!grouped[accountId]) {
        const account = this.getAccountFromId(affectedAccounts, item.accountId);
        grouped[accountId] = {
          accountId: item.accountId,
          accountName: account ? this.resolveAccountName(account, accountType) : undefined,
          instructions: []
        };
      }
      grouped[accountId].instructions.push(item);
    }
    return Object.values(grouped);
  }

  private static getAccountFromId(
    affectedAccounts: InvestmentInstructionSubmissionAffectedAccount[],
    accountId: number
  ): InvestmentInstructionSubmissionAffectedAccount | undefined {
    return affectedAccounts.find(account => account.accountId === accountId);
  }
}
